import { useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";

import type { Participant } from "@/api/api.types";
import { useUserStore } from "@/stores";
import { Checkbox } from "@/ui/form/Checkbox";
import { VideoSubmissionsModal } from "../VideoSubmissionsModal";

const formatDate = (value?: string | null) =>
  value
    ? new Date(value).toLocaleDateString("es-ES", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        timeZone: "UTC",
      })
    : "-";

const VideoSubmissionsCell = ({
  participant,
}: {
  participant: Participant;
}) => {
  const [showModal, setShowModal] = useState(false);
  const { videoSubmissions } = participant;

  if (!videoSubmissions) return "-";

  return (
    <>
      <button
        type="button"
        className="font-medium text-violet-900 underline underline-offset-2"
        onClick={() => setShowModal(true)}
      >
        {videoSubmissions.total}
      </button>
      <VideoSubmissionsModal
        show={showModal}
        onClose={() => setShowModal(false)}
        patientName={participant.patient?.name}
        videoSubmissions={videoSubmissions}
      />
    </>
  );
};

export const ActiveParticipantsColumns: () => ColumnDef<Participant>[] = () => {
  const { role } = useUserStore();

  const columns: ColumnDef<Participant>[] = [
    {
      id: "select",
      cell: ({ row }) =>
        row.original.studiesNames &&
        row.original.studiesNames.length > 0 && (
          <Checkbox
            id={row.id}
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
        ),
      enableHiding: false,
    },
    {
      id: "id",
      accessorFn: (participant) => participant.patient?.id,
      header: "ID",
    },
    {
      id: "name",
      accessorFn: (participant) => participant.patient?.name,
      header: "Name",
    },
    {
      id: "email",
      accessorFn: (participant) => participant.patient?.email,
      header: "Email",
    },
    {
      id: "age",
      accessorFn: (participant) => participant.patient?.age,
      header: "Age",
    },
    {
      id: "sex",
      accessorFn: (participant) => participant.patient?.sex,
      header: "Sex",
    },
    {
      id: "country",
      accessorFn: (participant) => participant.patient?.country,
      header: "Country",
    },
    {
      id: "createdAt",
      accessorFn: (participant) => formatDate(participant.patient?.createdAt),
      header: "Account created",
    },
    {
      id: "assessmentCompletedAt",
      accessorFn: (participant) =>
        formatDate(participant.assessmentCompletedAt),
      header: "Assessment completed",
    },
    {
      id: "videoSubmissions",
      header: "Video submissions",
      cell: ({ row }) => <VideoSubmissionsCell participant={row.original} />,
    },
  ];

  if (role === "admin") {
    columns.push({
      id: "research",
      accessorFn: (participant) =>
        participant.studiesNames && participant.studiesNames.length > 0
          ? participant.studiesNames
          : "No associated studies",
      header: "Associated research studies",
    });
  }

  return columns;
};
